import { useEffect, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { toast } from 'sonner'
import { Check, Link2 } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { FormError } from '../ui/FormError'
import { Spinner } from '@/app/lib/loader/loader'
import {
  navigationService,
  StoreNavigationItem,
  StoreNavigationItemType,
  StoreNavigationTargetType,
} from '../../services/navigation.service'
import { catalogSearchService } from '../../services/catalog-search.service'
import { promotionService } from '../../services/promotion.service'

// ──── Constantes de etiquetas ────────────────────────────────────────────────
const ITEM_TYPE_LABELS: Record<StoreNavigationItemType, string> = {
  LINK: 'Enlace',
  CATEGORY_SELECTOR: 'Selector de Categorías',
}

const TARGET_TYPE_LABELS: Record<StoreNavigationTargetType, string> = {
  PRODUCT: 'Producto',
  CATEGORY: 'Categoría',
  DEPARTMENT: 'Departamento',
  PRODUCT_TYPE: 'Tipo de Producto',
  PROMOTIONS: 'Sección de Promociones',
  URL: 'URL personalizada',
}

// Targets que requieren seleccionar un ID del catálogo
const TARGETS_WITH_ID: StoreNavigationTargetType[] = [
  'PRODUCT',
  'CATEGORY',
  'DEPARTMENT',
  'PRODUCT_TYPE',
]

// Mapeo catalog-search → catalog key
const TARGET_CATALOG: Partial<Record<StoreNavigationTargetType, string>> = {
  PRODUCT: 'products',
  CATEGORY: 'categories',
  DEPARTMENT: 'departments',
  PRODUCT_TYPE: 'product_types',
}

// ──── Schema Zod ─────────────────────────────────────────────────────────────
const itemSchema = z
  .object({
    label: z.string().min(1, 'La etiqueta es requerida').max(255),
    item_type: z.enum(['LINK', 'CATEGORY_SELECTOR']),
    target_type: z.string().nullable().optional(),
    target_id: z.number().nullable().optional(),
    target_url: z.string().max(2048).nullable().optional(),
    promotion_id: z.number().nullable().optional(),
    is_active: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.item_type === 'LINK') {
      if (!data.target_type) {
        ctx.addIssue({ code: 'custom', path: ['target_type'], message: 'El tipo de destino es obligatorio.' })
        return
      }
      if (TARGETS_WITH_ID.includes(data.target_type as StoreNavigationTargetType) && !data.target_id) {
        ctx.addIssue({ code: 'custom', path: ['target_id'], message: 'Debes seleccionar un destino.' })
      }
      if (data.target_type === 'URL' && !data.target_url) {
        ctx.addIssue({ code: 'custom', path: ['target_url'], message: 'La URL de destino es obligatoria.' })
      }
      if (data.target_type === 'PROMOTIONS' && !data.promotion_id) {
        ctx.addIssue({
          code: 'custom',
          path: ['promotion_id'],
          message: 'Debes seleccionar una promoción.',
        })
      }
    }
  })

type ItemFormValues = z.infer<typeof itemSchema>

// ──── Componente ─────────────────────────────────────────────────────────────
interface NavigationItemModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  menuId: number
  item: StoreNavigationItem | null // null = crear
  onSuccess: () => void
}

export function NavigationItemModal({
  open,
  onOpenChange,
  menuId,
  item,
  onSuccess,
}: NavigationItemModalProps) {
  const isEditing = !!item
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ItemFormValues>({
    resolver: zodResolver(itemSchema),
    defaultValues: {
      label: '',
      item_type: 'LINK',
      target_type: null,
      target_id: null,
      target_url: null,
      promotion_id: null,
      is_active: true,
    },
  })

  const itemType = watch('item_type')
  const targetType = watch('target_type') as StoreNavigationTargetType | null
  const isActiveValue = watch('is_active')

  // Búsqueda de entidades para target_id
  const [targetSearch, setTargetSearch] = useState('')
  const [catalogPage, setCatalogPage] = useState(1)
  const [allCatalogOptions, setAllCatalogOptions] = useState<{ id: number; name: string }[]>([])
  const [hasMoreCatalog, setHasMoreCatalog] = useState(true)
  // Nombre legible del ítem seleccionado (se usa al editar para pre-mostrar)
  const [selectedTargetName, setSelectedTargetName] = useState<string | null>(null)
  const catalogListRef = useRef<HTMLDivElement>(null)
  const catalogKey = targetType ? TARGET_CATALOG[targetType] : undefined

  const { data: catalogPageData, isFetching: isFetchingCatalog } = useQuery({
    queryKey: ['nav-catalog', catalogKey, targetSearch, catalogPage],
    queryFn: () => catalogSearchService.search(catalogKey!, targetSearch, catalogPage),
    enabled: !!catalogKey,
  })

  // Acumular resultados de catálogo conforme cambia la página
  useEffect(() => {
    if (!catalogPageData) return
    const incoming = catalogPageData as { id: number; name: string }[]
    if (catalogPage === 1) {
      setAllCatalogOptions(incoming)
      // En modo edición: si ya tenemos target_id pero aún no tenemos nombre,
      // buscamos en los resultados que acaban de llegar y capturamos el nombre.
      const currentId = watch('target_id')
      if (currentId && !selectedTargetName) {
        const found = incoming.find((o) => o.id === currentId)
        if (found) setSelectedTargetName(found.name)
      }
    } else {
      setAllCatalogOptions((prev) => {
        const existingIds = new Set(prev.map((o) => o.id))
        return [...prev, ...incoming.filter((o) => !existingIds.has(o.id))]
      })
    }
    // Si llegaron menos ítems del esperado, ya no hay más páginas
    setHasMoreCatalog(incoming.length >= 15)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogPageData, catalogPage])

  // Resetear paginación cuando cambia la búsqueda o el tipo
  useEffect(() => {
    setAllCatalogOptions([])
    setCatalogPage(1)
    setHasMoreCatalog(true)
  }, [catalogKey, targetSearch])

  // Scroll infinito en la lista de resultados
  useEffect(() => {
    const el = catalogListRef.current
    if (!el) return
    const onScroll = () => {
      if (!hasMoreCatalog || isFetchingCatalog) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
        setCatalogPage((p) => p + 1)
      }
    }
    el.addEventListener('scroll', onScroll)
    return () => el.removeEventListener('scroll', onScroll)
  }, [hasMoreCatalog, isFetchingCatalog])

  // Carga de promociones
  const { data: promotionsData } = useQuery({
    queryKey: ['promotions-options', 'ecommerce'],
    queryFn: () => promotionService.list({ channel: 'ecommerce', per_page: 100 }),
    enabled: targetType === 'PROMOTIONS',
  })
  const promotions = promotionsData?.data ?? []

  const selectedTargetId = watch('target_id')
  // El ítem seleccionado puede estar en la lista acumulada o guardar el nombre por separado
  const selectedTargetOption =
    allCatalogOptions.find((o) => o.id === selectedTargetId) ??
    (selectedTargetId && selectedTargetName ? { id: selectedTargetId, name: selectedTargetName } : null)

  useEffect(() => {
    if (!open) {
      reset({
        label: '',
        item_type: 'LINK',
        target_type: null,
        target_id: null,
        target_url: null,
        promotion_id: null,
        is_active: true,
      })
      setTargetSearch('')
      setSelectedTargetName(null)
      setAllCatalogOptions([])
      setCatalogPage(1)
    }
  }, [open, reset])

  useEffect(() => {
    if (item && open) {
      reset({
        label: item.label,
        item_type: item.item_type,
        target_type: item.target_type,
        target_id: item.target_id,
        target_url: item.target_url,
        promotion_id: item.promotion_id,
        is_active: item.is_active,
      })
      // Si el ítem tiene un target_id, iniciamos la búsqueda para precargar opciones
      if (item.target_id && item.target_type && TARGETS_WITH_ID.includes(item.target_type as StoreNavigationTargetType)) {
        setTargetSearch('')
        setAllCatalogOptions([])
        setCatalogPage(1)
      }
    }
  }, [item, open, reset])

  // Al cambiar item_type limpiamos campos de destino
  /*
  useEffect(() => {
    setValue('target_type', null)
    setValue('target_id', null)
    setValue('target_url', null)
    setValue('promotion_id', null)
  }, [itemType, setValue])
  */

  // Al cambiar target_type limpiamos target_id/url
  /*
  useEffect(() => {
    setValue('target_id', null)
    setValue('target_url', null)
    setValue('promotion_id', null)
    setTargetSearch('')
  }, [targetType, setValue])
  */

  const onSubmit = async (data: ItemFormValues) => {
    try {
      const payload = {
        ...data,
        target_type: (data.target_type as StoreNavigationTargetType | null) ?? null,
        promotion_id: data.target_type === 'PROMOTIONS'
          ? data.promotion_id
          : null,
      }

      if (isEditing && item) {
        await navigationService.updateItem(menuId, item.id, payload)
        queryClient.invalidateQueries({ queryKey: ['navigation-menu', menuId] })
        toast.success('Ítem actualizado exitosamente')
      } else {
        await navigationService.createItem(menuId, payload)
        toast.success('Ítem creado exitosamente')
      }
      onSuccess()
    } catch {
      toast.error('Ocurrió un error al guardar el ítem')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
        <form onSubmit={handleSubmit(onSubmit)} className="min-w-0 w-full overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-card-foreground">
              <Link2 className="h-5 w-5 text-primary" />
              {isEditing ? 'Editar Enlace' : 'Nuevo Enlace'}
            </DialogTitle>
            <DialogDescription className="text-card-description">
              Configura un ítem de navegación para este menú.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Label */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-card-foreground">
                Etiqueta <span className="text-red-500">*</span>
              </label>
              <Input placeholder="Ej. Inicio, Categorías, Ofertas..." {...register('label')} />
              <FormError message={errors.label?.message} />
            </div>

            {/* Tipo de ítem */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-card-foreground">
                Tipo <span className="text-red-500">*</span>
              </label>
              <Controller
                control={control}
                name="item_type"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(v) => {
                      const value = v as StoreNavigationItemType

                      field.onChange(value)

                      if (value !== 'LINK') {
                        setValue('target_type', null)
                        setValue('target_id', null)
                        setValue('target_url', null)
                        setValue('promotion_id', null)
                        setTargetSearch('')
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ITEM_TYPE_LABELS).map(([val, label]) => (
                        <SelectItem key={val} value={val}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <p className="text-xs text-muted-foreground">
                {itemType === 'CATEGORY_SELECTOR'
                  ? 'Muestra un mega-menú con todas las categorías disponibles.'
                  : 'Enlaza a una página, producto, categoría o URL específica.'}
              </p>
              <FormError message={errors.item_type?.message} />
            </div>

            {/* Destino (solo si es LINK) */}
            {itemType === 'LINK' && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-card-foreground">
                    Tipo de destino <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    control={control}
                    name="target_type"
                    render={({ field }) => (
                      <Select
                        value={field.value ?? ''}
                        onValueChange={(v) => {
                          const value = v === ''
                            ? null
                            : (v as StoreNavigationTargetType)

                          field.onChange(value)

                          setValue('target_id', null)
                          setValue('target_url', null)
                          setValue('promotion_id', null)
                          setTargetSearch('')
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona el destino" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(TARGET_TYPE_LABELS).map(([val, label]) => (
                            <SelectItem key={val} value={val}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <FormError message={errors.target_type?.message} />
                </div>
                {targetType === 'PROMOTIONS' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-card-foreground">
                      Promoción <span className="text-red-500">*</span>
                    </label>

                    <Controller
                      control={control}
                      name="promotion_id"
                      render={({ field }) => (
                        <Select
                          value={field.value?.toString() ?? ''}
                          onValueChange={(v) => field.onChange(v ? Number(v) : null)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecciona una promoción" />
                          </SelectTrigger>

                          <SelectContent>
                            {promotions.map((p) => (
                              <SelectItem key={p.id} value={String(p.id)}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />

                    <FormError message={errors.promotion_id?.message} />
                  </div>
                )}

                {/* URL personalizada */}
                {targetType === 'URL' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-card-foreground">
                      URL <span className="text-red-500">*</span>
                    </label>
                    <Input
                      placeholder="https://ejemplo.com/pagina"
                      {...register('target_url')}
                    />
                    <FormError message={errors.target_url?.message} />
                  </div>
                )}

                {/* Selector de entidad por ID */}
                {targetType && TARGETS_WITH_ID.includes(targetType) && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-card-foreground">
                      {TARGET_TYPE_LABELS[targetType]} <span className="text-red-500">*</span>
                    </label>

                    {/* Opción seleccionada (chip) */}
                    {selectedTargetOption && (
                      <div className="flex items-center gap-2 min-w-0 overflow-hidden rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                        <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                        <span className="text-sm font-medium text-primary flex-1 min-w-0 truncate">
                          {selectedTargetOption.name}
                        </span>
                        <button
                          type="button"
                          className="shrink-0 text-xs text-muted-foreground hover:text-destructive transition-colors"
                          onClick={() => {
                            setValue('target_id', null, { shouldValidate: true })
                            setSelectedTargetName(null)
                            setTargetSearch('')
                          }}
                        >
                          Cambiar
                        </button>
                      </div>
                    )}

                    {/* Buscador (solo visible si no hay selección) */}
                    {!selectedTargetOption && (() => {
                      // Mostramos spinner desde el primer instante (antes de que
                      // TanStack Query inicie el fetch) para evitar div vacío
                      const isFirstLoad = !!catalogKey && allCatalogOptions.length === 0
                      const showSpinner = isFetchingCatalog || isFirstLoad
                      return (
                        <>
                          <Input
                            placeholder={`Buscar ${TARGET_TYPE_LABELS[targetType].toLowerCase()}...`}
                            value={targetSearch}
                            onChange={(e) => setTargetSearch(e.target.value)}
                          />
                          <div
                            ref={catalogListRef}
                            className="max-h-52 min-h-[2.5rem] overflow-y-auto rounded-md border border-border"
                          >
                            {allCatalogOptions.length > 0
                              ? allCatalogOptions.map((opt) => (
                                  <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() => {
                                      setValue('target_id', opt.id, { shouldValidate: true })
                                      setSelectedTargetName(opt.name)
                                    }}
                                    className="w-full px-3 py-2 text-left text-sm transition-colors hover:bg-muted text-card-foreground"
                                  >
                                    {opt.name}
                                  </button>
                                ))
                              : !showSpinner && targetSearch.length > 0 && (
                                  <p className="px-3 py-2 text-xs text-muted-foreground">
                                    No se encontraron resultados.
                                  </p>
                                )}
                            {showSpinner && (
                              <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                                <Spinner className="h-3 w-3" /> Cargando...
                              </div>
                            )}
                          </div>
                        </>
                      )
                    })()}

                    <FormError message={errors.target_id?.message} />
                  </div>
                )}
              </>
            )}

            {/* Estado activo */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={isActiveValue}
                onClick={() => setValue('is_active', !isActiveValue)}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActiveValue ? 'bg-primary' : 'bg-slate-200'
                  }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActiveValue ? 'translate-x-2' : '-translate-x-2'
                    }`}
                />
              </button>
              <span className="text-sm font-medium text-card-foreground">Ítem Activo</span>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-end mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90"
            >
              {isSubmitting ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
